/**
 * Installing the app on a phone (I2): Chrome announces with `beforeinstallprompt` that it can install Drang,
 * possibly before any page is drawn, so the event is kept from the start (imported by `main.tsx`).
 */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // our own button asks instead of Chrome's mini bar
    deferred = e as InstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
}

export const canInstall = () => deferred !== null;

export function onInstallChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Shows Chrome's install dialog; true when the person installed the app. */
export async function install(): Promise<boolean> {
  if (!deferred) return false;
  const ev = deferred;
  deferred = null;
  await ev.prompt();
  const { outcome } = await ev.userChoice;
  listeners.forEach((l) => l());
  return outcome === "accepted";
}

export type PhoneBrowser = "android-chrome" | "android-other" | "ios" | "other";

/** Which browser this is, as far as installing goes. */
export function phoneBrowser(ua: string = navigator.userAgent): PhoneBrowser {
  if (/iPhone|iPad|iPod/.test(ua)) return "ios";
  if (!/Android/.test(ua)) return "other";
  const notChrome = /SamsungBrowser|EdgA|OPR|Firefox|YaBrowser|DuckDuckGo|; wv\)/.test(ua);
  return /Chrome\//.test(ua) && !notChrome ? "android-chrome" : "android-other";
}

/** Android's way of opening a page in a given app: here Chrome, from any other browser. */
export const chromeIntent = (host: string, path: string) =>
  `intent://${host}${path}#Intent;scheme=https;package=com.android.chrome;end`;

/** An address only this computer can open (a phone would not reach it). */
export const isLocalOnly = (hostname: string) =>
  hostname === "localhost" ||
  hostname.endsWith(".localhost") ||
  /^127\./.test(hostname) ||
  hostname === "::1";

export const isStandalone = () =>
  typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches;
